<template>
<v-container>
    <v-card class="mt-5">
        <v-form ref="form">
            <div>
                <v-row class="primary-header pa-3 ma-0" align="center">
                    <span style="font-size: 25px">{{ title }}</span>
                </v-row>
                <v-col>
                    <div class="mb-3" style="font-size: 18px">{{ description }}</div>
                    <div class="mx-2 my-4" style="font-size: 15px" v-for="text in texts" :key="text">
                        {{ text }}
                    </div>
                </v-col>
            </div>

            <v-divider />

            <div class="text-right">
                <v-btn class="ma-2" small rounded color="primary" v-clipboard:copy="buildUrl()" v-clipboard:success="() => $toasted.success('클립보드에 복사되었습니다')">
                    <v-icon left>share</v-icon>공유
                </v-btn>
                <v-btn class="ma-2" small rounded color="primary" @click="showSaveDialog">
                    <v-icon left>save</v-icon>저장
                </v-btn>
                <v-btn class="ma-2" small rounded color="primary" @click="showLoadDialog">
                    <v-icon left>folder_open</v-icon>불러오기
                </v-btn>
            </div>

            <div class="pb-5">
                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        시작년도
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-select v-model="start" :items="years" solo hide-details suffix="년" />
                    </v-col>
                </v-row>

                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        종료년도
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-select v-model="end" :items="endYears" solo hide-details suffix="년" />
                    </v-col>
                </v-row>

                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        <div>리밸런싱 월</div>
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-select v-model="rebalanceMonth" :items="months" solo hide-details suffix="월" />
                    </v-col>
                </v-row>

                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        <div>최대 종목보유수</div>
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-text-field v-mask="'##'" v-model="max" solo suffix="종목" :rules="maxRule" hide-details="auto" />
                    </v-col>
                </v-row>

                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        시작금
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-text-field ref="cashField" v-model="cash" suffix="만원" solo @keypress="checkNumber" @input="onInput" :rules="cashRule" hide-details="auto" />
                    </v-col>
                </v-row>
                
                <v-row class="ma-3" align="center">
                    <v-col class="item-title pa-1" :cols="categoryRatio[0]" :sm="categoryRatio[1]">
                        <div>배당금</div>
                    </v-col>
                    <v-col class="pa-0" :cols="inputRatio[0]" :sm="inputRatio[1]" style="min-width: 300px">
                        <v-select v-model="isDividend" :items="dividendItems" item-text="text" value="value" solo hide-details />
                    </v-col>
                </v-row>

                <sector-board class="ma-4" ref="sectorBoard" />

                <condition-board class="ma-4" ref="conditionBoard" title="매수" />

                <div class="mt-5 text-center">
                    <v-btn color="primary" @click="startBackTest" :loading="loading">백테스트 시작</v-btn>
                    <div class="caption mt-2" v-if="loading">
                        <div>백테스트가 진행 중입니다.</div>
                        <div class="mt-2">1분 이상 소요될 수 있습니다.</div>
                    </div>
                    <div class="mt-4"><v-btn color="secondary" small @click="sponsor">개발자 후원</v-btn></div>
                </div>
            </div>
        </v-form>
    </v-card>

    <back-test-report :key="reportKey" ref="reports" />

    <save-dialog ref="saveDialog" />

    <load-dialog ref="loadDialog" @load="loadBackTest" />
    
    <sponsor-dialog ref="sponsorDialog" />
</v-container>
</template>

<script>
import firebase from 'firebase/app'
import {
    db,
    auth
} from '@/firebase'
import SectorBoard from '@/views/components/SectorBoard'
import ConditionBoard from '@/views/components/ConditionBoard'
import BackTestReport from '@/views/components/BackTestReport'
import SaveDialog from '@/views/dialogs/SaveDialog'
import LoadDialog from '@/views/dialogs/LoadDialog'
import SponsorDialog from '@/views/dialogs/SponsorDialog'
import {
    AccountToast
} from '@/mixins/account-toast.js'

export default {
    metaInfo: {
        title: "Invest Helper | 백테스트",
        htmlAttrs: {
            lang: "ko-KR"
        },
        meta: [{
                charset: "utf-8"
            },
            {
                name: "description",
                content: "20년치 코스피/코스닥 주식들의 백테스트 기능을 제공합니다"
            },
            {
                name: "keywords",
                content: "코스피, 코스닥, 백테스트"
            },
        ]
    },
    components: {
        SectorBoard,
        ConditionBoard,
        BackTestReport,
        SaveDialog,
        LoadDialog,
        SponsorDialog
    },
    mixins: [
        AccountToast
    ],
    data: () => ({
        reportKey: 0,
        categoryRatio: [12, 2],
        inputRatio: [12, 3],
        disableDraggable: false,
        title: '국내주식 퀀트 백테스트',
        description: '국내주식 (코스피, 코스닥)에 대한 20년치 퀀트 기반 백테스트 기능을 제공하며, 백테스트에 적용되는 조건은 아래와 같습니다.',
        texts: [
            '1. 주가는 매월 말일 종가 기준입니다',
            '2. 보통주만 거래할 수 있습니다 (우선주의 시가총액은 보통주에 더해짐)',
            '3. 배당에 세금은 적용되지 않습니다',
            '4. 상장폐지 등의 이유로 거래정지된 종목은 최종 가격으로 매도된 후 기존 매수된 종목들로 재투자됩니다',
        ],
        years: [2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022],
        months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        start: 2010,
        end: 2022,
        rebalanceMonth: 3,
        isDividend: 'yes',
        dividendItems: [{
            text: '고려',
            value: 'yes'
        }, {
            text: '미고려',
            value: 'no'
        }],
        startMenu: false,
        endMenu: false,
        rebalanceMenu: false,
        max: 10,
        cash: '100',
        sortList: [],
        orderList: [],
        ratios: [],
        ratiosReversed: [],
        percents: [],
        percentsReversed: [],
        buyToolTip: false,
        buyDialog: false,
        saveDialog: false,
        loading: false,
        maxRule: [
            v => {
                if (!v || v < 1 || v > 20) return "종목은 1개 이상, 20개 이하여야 합니다"
                return true
            }
        ],
        cashRule: [
            v => {
                if (!v) return "시작금은 100만원 이상, 100억원 이하여야 합니다"

                const cash = parseInt(v.split(",").join(""))
                if (cash < 100 || cash > 1000000) return "시작금은 100만원 이상, 100억원 이하여야 합니다"

                return true
            }
        ],
    }),
    computed: {
        user() {
            return this.$store.getters.user
        },
        endYears() {
            return this.years.filter(x => x >= this.start)
        },
    },
    watch: {
        start(value) {
            const startYear = parseInt(value)
            const endYear = parseInt(this.end)
            if (startYear >= endYear) this.end = startYear
        }
    },
    created() {},
    async mounted() {
        if (localStorage.getItem("backtest")) {
            this.loadBackTest(localStorage.getItem("backtest"))
            localStorage.removeItem("backtest")
        }
    },
    methods: {
        showSaveDialog() {
            if (!this.checkAvailableAccount()) return

            this.$refs.saveDialog.show('quant', this.buildUrl())
        },
        showLoadDialog() {
            if (!this.checkAvailableAccount()) return

            this.$refs.loadDialog.show('quant')
        },
        loadBackTest(url) {
            try {
                const rawData = url.substring(url.indexOf("?") + 1).split("&")
                const loadedObject = {}
                for (let i = 0; i < rawData.length; i++) {
                    const item = rawData[i].split("=")
                    loadedObject[item[0]] = item[1]
                }

                // Update view-model
                for (let i = 0; i < rawData.length; i++) {
                    const item = rawData[i].split("=")
                    const key = item[0]
                    const value = item[1]
                }

                this.start = parseInt(loadedObject.start)
                this.end = parseInt(loadedObject.end)
                this.max = parseInt(loadedObject.max)
                this.cash = loadedObject.cash
                this.rebalanceMonth = parseInt(loadedObject.month)
                this.isDividend = loadedObject.dividend
                
                const checkedMarkets = loadedObject.market.split(",")
                this.$refs.sectorBoard.markets.forEach(m => m.value = checkedMarkets.includes(`${m.id}`))

                const checkedSectors = loadedObject.sector.split(",")
                this.$refs.sectorBoard.sectors.forEach(s => s.value = checkedSectors.includes(`${s.id}`))

                this.$refs.conditionBoard.load(loadedObject)
            } catch (e) {
                this.$toasted.error('불러오기에 실패했습니다')
                if (process.env.NODE_ENV == "production") this.$http.post('/report-error', {stack: e.stack})
                else console.log(e)
            }
        },
        getRandomInt() {
            return Math.floor(Math.random() * (50 - 5 + 1)) + 5
        },
        async startBackTest() {
            if (!this.$refs.form.validate()) return this.$toasted.error("백테스트 조건을 다시 확인해주세요")

            if (!this.$refs.conditionBoard.validate()) return
            
            if (!this.$refs.sectorBoard.validate()) return

            this.reportKey++
            this.loading = true
            this.$store.state.backTestUrl = this.buildUrl()

            try {
                const result = await this.$http.get(this.$store.state.backTestUrl)
                
                if (result.data.length == 0) return alert('일치하는 주식을 찾을 수 없습니다.\n\n 조건을 완화한 후 다시 시도해주세요')

                this.$toasted.info('백테스트가 완료되었습니다')
                console.log(result.data)
                this.$refs.reports.updateReport(result.data, this.sortList, this.orderList, this.cash, this.start, this.end, this.rebalanceMonth)
            } catch (e) {
                console.log(e)
                this.$toasted.info('백테스트 도중 오류가 발생했습니다')
                if (process.env.NODE_ENV == "production") this.$http.post('/report-error', {stack: e.stack})
            } finally {
                this.loading = false
            }
        },
        buildUrl() {
            if (!this.$refs.conditionBoard) return ''

            let url = `/back-test?start=${this.start}&end=${this.end}&month=${this.rebalanceMonth}&max=${this.max}&cash=${this.cash.replace(/[^0-9]/g, '')}&dividend=${this.isDividend}&`

            let sort = ''
            let order = ''
            for (let item of this.$refs.conditionBoard.sortedConditions) {
                const min = Math.min(item.value[0], item.value[1])
                const max = Math.max(item.value[0], item.value[1])
                url += `${item.id}Min=${min.toFixed(1)}&${item.id}Max=${max.toFixed(1)}&`
                sort += `${item.id},`
                order += `${item.order},`
            }

            sort = sort.substring(0, sort.length - 1)
            order = order.substring(0, order.length - 1)
            this.sortList = sort.split(',')
            this.orderList = order.split(',')

            url += `sort=${sort}&order=${order}&market=${this.$refs.sectorBoard.activeMarketIdsText}&sector=${this.$refs.sectorBoard.activeSectorIdsText}`

            return url 
        },
        checkNumber($event) {
            const number = $event.key.replace(/[^0-9]/g, '')
            if (!number) {
                return $event.preventDefault()
            }
        },
        onInput(value) {
            const numbers = value.replace(/[^0-9]/g, '')
            this.cash = numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        },
        checkAvailableAccount() {
            if (!this.$store.getters.isLoggedIn) {
                localStorage.setItem("backtest", this.buildUrl())
                this.showLoginToast()
                return false
            }

            if (!this.$store.state.emailVerified) {
                localStorage.setItem("backtest", this.buildUrl())
                this.showVerifyToast()
                return false
            }

            return true
        },
        sponsor() {
            if (this.$store.state.mobile) window.open('https://qr.kakaopay.com/2810060111039909710072079c402115')
            else this.$refs.sponsorDialog.show()
        }
    },
}
</script>

<style>
.error-message {
    font-size: 1em;
    color: red;
}
</style>
